import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AlertCircle, ArrowRight, CheckCircle2, Loader2, MapPinned, MessageSquareText, Search, Sparkles, Store, Wand2, XCircle } from "lucide-react";
import {
  getStarterTemplateByVertical,
  getStarterTemplatePage,
  listStarterTemplates,
} from "@ease/page-builder";
import {
  approveAiCheckpoint,
  formatAiGenerationError,
  getAiCheckpoint,
  modifyAiCheckpoint,
  rejectAiCheckpoint,
  streamAiPageGeneration,
  useBusinessEnrichment,
  type AiCheckpointDetail,
  type BusinessInterviewInput,
  type ConfirmedBusinessInput,
  type PuckComponent,
  type StarterContextInput,
} from "@/hooks/use-page-ai";
import {
  buildSectionReviewInstruction,
  getSectionApprovalSummary,
  getSectionReviewKey,
  getSectionReviewSummary,
  getSectionReviewTitle,
  type SectionReviewChoice,
} from "@/pages/page-builder/section-review";
import { sendAiFeedbackSignal } from "@/lib/ai-feedback";
import { useTranslation } from "@/i18n";

const PAGE_TYPE_LABELS: Record<string, string> = {
  site_page: "info page",
  lead_capture: "lead page",
  sales: "sales page",
  booking: "booking page",
  webinar: "webinar page",
  thank_you: "thank-you page",
};

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function parseList(value: string) {
  return value
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function extractSections(checkpoint: AiCheckpointDetail | null) {
  if (!checkpoint) {
    return [];
  }

  const currentVersion = checkpoint.versions.find(
    (version) => version.versionNumber === checkpoint.currentVersion,
  );
  return currentVersion?.output.puckData?.content ?? [];
}

function extractUnmappedCount(checkpoint: AiCheckpointDetail | null) {
  if (!checkpoint) {
    return 0;
  }

  const currentVersion = checkpoint.versions.find(
    (version) => version.versionNumber === checkpoint.currentVersion,
  );
  return currentVersion?.output.unmappedFields?.length ?? 0;
}

function buildStarterContext(
  template: ReturnType<typeof getStarterTemplateByVertical> | undefined,
  starter: ReturnType<typeof getStarterTemplatePage> | undefined,
): StarterContextInput | undefined {
  if (!template || !starter) {
    return undefined;
  }

  return {
    templateName: template.name,
    templateDescription: template.description,
    starterPageTitle: starter.title,
    starterPageKind: starter.kind,
    starterPageSummary: starter.aiSummary,
    sectionTypes: starter.content
      .map((section) => section.type)
      .filter((type): type is string => typeof type === "string" && type.length > 0),
  };
}

function buildAiGenerateHref(input: {
  template?: string | null;
  starter?: string;
  siteId?: string;
  source?: string | null;
}) {
  const search = new URLSearchParams();

  if (input.template) {
    search.set("template", input.template);
  }
  if (input.starter) {
    search.set("starter", input.starter);
  }
  if (input.siteId) {
    search.set("siteId", input.siteId);
  }
  if (input.source) {
    search.set("source", input.source);
  }

  const query = search.toString();
  return query ? `/pages/ai-generate?${query}` : "/pages/ai-generate";
}

function buildEditorContinuationHref(pageId: string, input: {
  siteId?: string;
  entry: "generated" | "template";
}) {
  const search = new URLSearchParams({
    mode: "simple",
    entry: input.entry,
  });

  if (input.siteId) {
    search.set("siteId", input.siteId);
  }

  return `/pages/editor/${pageId}?${search.toString()}`;
}

function getDraftReadinessHint(input: {
  interviewReady: boolean;
  businessDecision: "pending" | "confirmed" | "manual";
  checkpoint: AiCheckpointDetail | null;
}) {
  if (!input.interviewReady) {
    return "Complete the business basics first.";
  }

  if (input.businessDecision === "pending") {
    return "Confirm the business details or choose to use your answers only.";
  }

  if (input.checkpoint) {
    return "You can create a fresh draft again, or keep revising the current one below.";
  }

  return "Ready to generate a review draft.";
}

export default function AiGeneratePage() {
  const navigate = useNavigate();
  const { locale } = useTranslation();
  const [searchParams] = useSearchParams();
  const templateVertical = searchParams.get("template");
  const starterPageId = searchParams.get("starter") ?? undefined;
  const siteId = searchParams.get("siteId") ?? undefined;
  const source = searchParams.get("source");
  const isFirstRun = source === "onboarding";
  const selectedTemplate = templateVertical ? getStarterTemplateByVertical(templateVertical) : undefined;
  const selectedStarter = templateVertical ? getStarterTemplatePage(templateVertical, starterPageId) : undefined;
  const availableTemplates = useMemo(() => listStarterTemplates(), []);
  const starterPages = selectedTemplate?.pages ?? [];

  const [businessName, setBusinessName] = useState("");
  const [businessType, setBusinessType] = useState(selectedTemplate?.name ?? "");
  const [location, setLocation] = useState("");
  const [primaryOffer, setPrimaryOffer] = useState("");
  const [idealCustomer, setIdealCustomer] = useState("");
  const [primaryGoal, setPrimaryGoal] = useState("");
  const [differentiatorsInput, setDifferentiatorsInput] = useState("");
  const [mustIncludeInput, setMustIncludeInput] = useState("");
  const [pageType, setPageType] = useState<string>(selectedStarter?.pageType ?? "site_page");
  const [confirmedBusiness, setConfirmedBusiness] = useState<ConfirmedBusinessInput | null>(null);
  const [businessDecision, setBusinessDecision] = useState<"pending" | "confirmed" | "manual">("pending");
  const [checkpoint, setCheckpoint] = useState<AiCheckpointDetail | null>(null);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
  const [iterationNotes, setIterationNotes] = useState("");
  const [surfaceError, setSurfaceError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isIterating, setIsIterating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastAction, setLastAction] = useState<string | null>(null);
  const [approvedPageId, setApprovedPageId] = useState<string | null>(null);
  const [streamedSections, setStreamedSections] = useState<PuckComponent[]>([]);
  const [streamProgress, setStreamProgress] = useState<{ current: number; total: number } | null>(null);
  const [sectionDecisions, setSectionDecisions] = useState<Record<string, SectionReviewChoice>>({});
  const [sectionFeedback, setSectionFeedback] = useState<Record<string, string>>({});
  const [activeFeedbackKey, setActiveFeedbackKey] = useState<string | null>(null);
  const [sectionActionKey, setSectionActionKey] = useState<string | null>(null);
  const [draftFeedbackRating, setDraftFeedbackRating] = useState<number | null>(null);
  const {
    data: enrichmentResult,
    isLoading: isLookingUpBusiness,
    error: enrichmentError,
    lookup: lookupBusiness,
  } = useBusinessEnrichment();

  const sections = useMemo(() => extractSections(checkpoint), [checkpoint]);
  const unmappedCount = useMemo(() => extractUnmappedCount(checkpoint), [checkpoint]);
  const starterContext = useMemo(
    () => buildStarterContext(selectedTemplate, selectedStarter),
    [selectedStarter, selectedTemplate],
  );
  const sectionReviewKeys = useMemo(() => {
    if (!checkpoint) {
      return [];
    }

    return sections.map((section, index) => getSectionReviewKey(checkpoint.currentVersion, section, index));
  }, [checkpoint, sections]);
  const sectionApproval = useMemo(() => {
    return getSectionApprovalSummary(sectionReviewKeys, sectionDecisions);
  }, [sectionDecisions, sectionReviewKeys]);

  useEffect(() => {
    if (selectedStarter) {
      setPageType(selectedStarter.pageType);
    }
  }, [selectedStarter]);

  useEffect(() => {
    if (selectedTemplate && !businessType.trim()) {
      setBusinessType(selectedTemplate.name);
    }
  }, [businessType, selectedTemplate]);

  useEffect(() => {
    setActiveFeedbackKey(null);
    setSectionActionKey(null);
  }, [checkpoint?.currentVersion]);

  useEffect(() => {
    setSectionDecisions({});
    setSectionFeedback({});
    setActiveFeedbackKey(null);
    setSectionActionKey(null);
    setDraftFeedbackRating(null);
  }, [checkpoint?.id]);

  const interviewReady =
    businessName.trim().length >= 2 &&
    businessType.trim().length >= 2 &&
    location.trim().length >= 2 &&
    primaryOffer.trim().length >= 10 &&
    idealCustomer.trim().length >= 10 &&
    primaryGoal.trim().length >= 10;
  const businessLookupReady =
    businessName.trim().length >= 2 &&
    businessType.trim().length >= 2 &&
    location.trim().length >= 2;

  const draftReadinessHint = useMemo(() => getDraftReadinessHint({
    interviewReady,
    businessDecision,
    checkpoint,
  }), [businessDecision, checkpoint, interviewReady]);
  const progressSteps = useMemo(() => ([
    {
      id: "interview",
      label: "Business basics",
      done: interviewReady,
      active: !interviewReady,
    },
    {
      id: "confirm",
      label: "Confirm details",
      done: businessDecision !== "pending",
      active: interviewReady && businessDecision === "pending",
    },
    {
      id: "review",
      label: "Review draft",
      done: Boolean(checkpoint && sectionApproval.allApproved),
      active: businessDecision !== "pending" && !approvedPageId,
    },
    {
      id: "editor",
      label: "Continue in editor",
      done: Boolean(approvedPageId),
      active: Boolean(checkpoint && sectionApproval.allApproved && !approvedPageId),
    },
  ]), [approvedPageId, businessDecision, checkpoint, interviewReady, sectionApproval.allApproved]);

  const canGenerate = interviewReady && businessDecision !== "pending";

  const interviewInput: BusinessInterviewInput = {
    businessName: businessName.trim(),
    businessType: businessType.trim(),
    location: location.trim(),
    primaryOffer: primaryOffer.trim(),
    idealCustomer: idealCustomer.trim(),
    primaryGoal: primaryGoal.trim(),
    differentiators: parseList(differentiatorsInput),
    mustInclude: parseList(mustIncludeInput),
  };

  const resetBusinessConfirmation = () => {
    setBusinessDecision("pending");
    setConfirmedBusiness(null);
    setLastAction(null);
  };

  const handleBusinessLookup = async () => {
    if (!businessLookupReady) {
      setSurfaceError("Answer question 1 before we look up the business details.");
      return;
    }

    setSurfaceError(null);
    setLastAction(null);

    const result = await lookupBusiness({
      businessName: interviewInput.businessName,
      businessType: interviewInput.businessType,
      location: interviewInput.location,
    });

    if (!result) {
      setBusinessDecision("pending");
      return;
    }

    if (result.found && result.business) {
      setConfirmedBusiness(result.business);
      setBusinessDecision("confirmed");
      setLastAction("We found business details. Review them below before creating the draft.");
      return;
    }

    setConfirmedBusiness(null);
    setBusinessDecision("manual");
    setLastAction("We could not confirm a listing. You can still create the draft from your answers.");
  };

  const handleGenerate = async () => {
    if (!canGenerate) {
      return;
    }

    setSurfaceError(null);
    setLastAction(null);
    setApprovedPageId(null);
    setCheckpoint(null);
    setStreamedSections([]);
    setStreamProgress(null);
    setIsGenerating(true);

    try {
      const result = await streamAiPageGeneration({
        pageType,
        siteId,
        locale,
        intakeMode: "two_question",
        interview: interviewInput,
        confirmedBusiness: businessDecision === "confirmed" ? confirmedBusiness ?? undefined : undefined,
        starterContext,
      }, {
        onStart: (event) => {
          setStreamProgress({ current: 0, total: event.totalSections });
          setLastAction(`Generating ${event.totalSections} sections from the two-question brief.`);
        },
        onSection: (event) => {
          setStreamedSections((current) => [...current, ...event.components]);
          setStreamProgress({ current: event.index + 1, total: event.totalSections });
        },
      });

      setIsRefreshing(true);
      const detail = await getAiCheckpoint(result.id);
      setCheckpoint(detail);
      const pageLabel = PAGE_TYPE_LABELS[pageType] ?? "page";
      const suggestedTitle = `${businessName.trim()} ${pageLabel}`;
      setTitle(suggestedTitle);
      setSlug(slugify(suggestedTitle));
      setIsSlugManuallyEdited(false);
      setLastAction("Your draft is ready. Review each section below. Nothing is saved until you approve it into the editor.");
    } catch (generationError) {
      setSurfaceError(formatAiGenerationError(generationError));
    } finally {
      setIsRefreshing(false);
      setIsGenerating(false);
    }
  };

  const handleIterate = async () => {
    if (!checkpoint || !iterationNotes.trim()) {
      return;
    }

    setIsIterating(true);
    setSurfaceError(null);
    try {
      await modifyAiCheckpoint(checkpoint.id, iterationNotes.trim());
      const detail = await getAiCheckpoint(checkpoint.id);
      setCheckpoint(detail);
      setIterationNotes("");
      setLastAction("Draft updated. Review the new version and confirm each section again before saving it to the editor.");
    } catch (iterationError) {
      setSurfaceError(formatAiGenerationError(iterationError));
    } finally {
      setIsIterating(false);
    }
  };

  const handleSectionDecision = (reviewKey: string, decision: SectionReviewChoice) => {
    setSectionDecisions((current) => ({ ...current, [reviewKey]: decision }));
    if (decision !== "change") {
      setActiveFeedbackKey((current) => (current === reviewKey ? null : current));
    }
  };

  const handleSectionChange = async (
    section: PuckComponent,
    index: number,
    reviewKey: string,
  ) => {
    if (!checkpoint) {
      return;
    }

    setSectionActionKey(reviewKey);
    setSurfaceError(null);

    try {
      const instruction = buildSectionReviewInstruction({
        component: section,
        index,
        mode: "change",
        feedback: sectionFeedback[reviewKey],
      });
      await modifyAiCheckpoint(checkpoint.id, instruction);
      const detail = await getAiCheckpoint(checkpoint.id);
      setCheckpoint(detail);
      setActiveFeedbackKey(null);
      setLastAction(`Section ${index + 1} updated. Review the changes and confirm the section again before saving.`);
    } catch (sectionError) {
      setSurfaceError(formatAiGenerationError(sectionError));
    } finally {
      setSectionActionKey(null);
    }
  };

  const handleSectionRedo = async (
    section: PuckComponent,
    index: number,
    reviewKey: string,
  ) => {
    if (!checkpoint) {
      return;
    }

    setSectionActionKey(reviewKey);
    setSurfaceError(null);

    try {
      const instruction = buildSectionReviewInstruction({
        component: section,
        index,
        mode: "redo",
      });
      await modifyAiCheckpoint(checkpoint.id, instruction);
      const detail = await getAiCheckpoint(checkpoint.id);
      setCheckpoint(detail);
      setActiveFeedbackKey(null);
      setLastAction(`Section ${index + 1} rebuilt from scratch. Review the new version and confirm it again before saving.`);
    } catch (sectionError) {
      setSurfaceError(formatAiGenerationError(sectionError));
    } finally {
      setSectionActionKey(null);
    }
  };

  const handleApprove = async () => {
    if (!checkpoint || checkpoint.status !== "pending" || !title.trim() || !slug.trim()) {
      return;
    }

    setIsApproving(true);
    setSurfaceError(null);
    try {
      const approved = await approveAiCheckpoint(checkpoint.id, {
        title: title.trim(),
        slug: slug.trim(),
        pageType,
        siteId,
      });
      const pageId = approved.page.id;
      setApprovedPageId(pageId);
      setLastAction(`Draft saved as "${approved.page.title}". Opening the editor now.`);
      navigate(buildEditorContinuationHref(pageId, {
        siteId,
        entry: selectedTemplate ? "template" : "generated",
      }));
    } catch (approvalError) {
      setSurfaceError(formatAiGenerationError(approvalError));
    } finally {
      setIsApproving(false);
    }
  };

  const handleNavigateToApprovedPage = () => {
    if (!approvedPageId) {
      return;
    }

    navigate(buildEditorContinuationHref(approvedPageId, {
      siteId,
      entry: selectedTemplate ? "template" : "generated",
    }));
  };

  const handleRefreshCheckpoint = async () => {
    if (!checkpoint) {
      return;
    }

    setIsRefreshing(true);
    setSurfaceError(null);

    try {
      const detail = await getAiCheckpoint(checkpoint.id);
      setCheckpoint(detail);
      setLastAction("Draft refreshed. Review the current version below.");
    } catch (refreshError) {
      setSurfaceError(formatAiGenerationError(refreshError));
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleReject = async () => {
    if (!checkpoint) {
      return;
    }

    setIsRejecting(true);
    setSurfaceError(null);
    try {
      await rejectAiCheckpoint(checkpoint.id);
      setCheckpoint(null);
      setIterationNotes("");
      setTitle("");
      setSlug("");
      setIsSlugManuallyEdited(false);
      setApprovedPageId(null);
      setLastAction("Draft discarded. You can start a new one from the interview above.");
    } catch (rejectionError) {
      setSurfaceError(formatAiGenerationError(rejectionError));
    } finally {
      setIsRejecting(false);
    }
  };

  const handleDraftFeedbackRating = async (rating: number) => {
    if (!checkpoint) {
      return;
    }

    try {
      await sendAiFeedbackSignal({
        signalType: "pages.ai_draft.rating",
        aiOutputId: `${checkpoint.id}:v${checkpoint.currentVersion}`,
        aiOutput: {
          checkpointId: checkpoint.id,
          versionNumber: checkpoint.currentVersion,
          sectionCount: sections.length,
          pageType,
        },
        humanAction: {
          rating,
        },
        context: {
          touchpoint: "pages.ai_generate.review",
        },
      });
      setDraftFeedbackRating(rating);
      setSurfaceError(null);
    } catch {
      setSurfaceError("The draft rating could not be recorded.");
    }
  };

  const businessCard = businessDecision === "pending"
    ? null
    : businessDecision === "confirmed"
      ? confirmedBusiness
      : enrichmentResult?.business ?? null;

  return (
    <div className="space-y-8">
      <section className="rounded-[28px] border border-border bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.14),_transparent_42%),linear-gradient(135deg,rgba(15,23,42,0.04),rgba(15,23,42,0))] p-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl space-y-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.24em] text-emerald-700">
              <Sparkles className="h-3.5 w-3.5" />
              {isFirstRun ? "First-Run Tell Me" : "Tell Me"}
            </span>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              Answer two questions. Confirm the business. Review the draft.
            </h1>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
              {isFirstRun
                ? "You chose the most guided path. Answer the two-question intake, review the generated draft section by section, and then continue into the editor."
                : "Answer the two-question intake, review the generated draft section by section, and then continue into the editor when it looks right."}
            </p>
            {selectedTemplate ? (
              <div className="rounded-2xl border border-border bg-background/80 px-4 py-3 text-sm text-muted-foreground shadow-sm">
                <span className="font-medium text-foreground">{selectedTemplate.name}</span>
                {selectedStarter ? ` · ${selectedStarter.title}` : ""}
                <div className="mt-1 leading-6">{selectedTemplate.description}</div>
              </div>
            ) : null}
            {isFirstRun ? (
              <div className="rounded-2xl border border-border bg-background/80 px-4 py-3 text-sm text-muted-foreground shadow-sm">
                Need a different level of help? You can still switch back to Pick One or Build It before generating the draft.
                <div className="mt-3">
                  <Link
                    to="/pages/new?source=onboarding"
                    className="inline-flex items-center gap-2 font-medium text-foreground transition hover:text-primary"
                  >
                    Back to first-run choices
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            ) : null}
          </div>

          {isFirstRun ? (
            <div className="rounded-2xl border border-border bg-background px-4 py-4 text-sm text-muted-foreground shadow-sm">
              Stay on this path until you have a saved draft. More advanced builders are still available later if you want them.
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Link
                to="/pages/streaming-generator"
                className="rounded-2xl border border-border bg-background px-4 py-3 text-sm transition hover:border-foreground/20 hover:bg-accent"
              >
                <span className="mb-1 flex items-center gap-2 font-medium text-foreground">
                  <Wand2 className="h-4 w-4" />
                  Streaming builder
                </span>
                <span className="text-muted-foreground">Watch sections arrive live, then keep shaping them in guided editing.</span>
              </Link>
              <Link
                to="/pages/conversational-builder"
                className="rounded-2xl border border-border bg-background px-4 py-3 text-sm transition hover:border-foreground/20 hover:bg-accent"
              >
                <span className="mb-1 flex items-center gap-2 font-medium text-foreground">
                  <MessageSquareText className="h-4 w-4" />
                  Conversational builder
                </span>
                <span className="text-muted-foreground">Use natural-language changes in the richer multi-panel builder.</span>
              </Link>
            </div>
          )}
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
        <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
          <div className="mb-5 space-y-1">
            <h2 className="text-lg font-semibold text-foreground">1. Answer the two-question intake</h2>
            <p className="text-sm text-muted-foreground">
              {isFirstRun
                ? "We use these two answers to build the first draft around the outcome you want. Keep it simple and factual."
                : "We use these two answers to build the first draft. Keep it simple and factual."}
            </p>
            {siteId ? (
              <p className="text-sm text-muted-foreground">
                The finished draft will stay attached to the selected site container.
              </p>
            ) : null}
          </div>

          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-border bg-background/70 px-4 py-3 md:col-span-2">
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Question 1</div>
                <h3 className="mt-1 font-medium text-foreground">What business are we building this website for?</h3>
                <p className="mt-1 text-sm text-muted-foreground">Name, category, and location are enough to run the Google business lookup.</p>
              </div>

              <label className="space-y-2 text-sm">
                <span className="font-medium text-foreground">Starting direction</span>
                <select
                  value={templateVertical ?? ""}
                  onChange={(event) => {
                    const nextVertical = event.target.value;
                    if (!nextVertical) {
                      navigate(buildAiGenerateHref({ siteId, source }), { replace: true });
                      return;
                    }

                    const nextStarter = getStarterTemplateByVertical(nextVertical)?.pages[0];
                    navigate(buildAiGenerateHref({
                      template: nextVertical,
                      starter: nextStarter?.id,
                      siteId,
                      source,
                    }), { replace: true });
                  }}
                  className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                >
                  <option value="">No starter selected</option>
                  {availableTemplates.map((template) => (
                    <option key={template.id} value={template.vertical}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-2 text-sm">
                <span className="font-medium text-foreground">{selectedTemplate ? "Starter page" : "Page type"}</span>
                {selectedTemplate ? (
                  <select
                    value={selectedStarter?.id ?? ""}
                    onChange={(event) => {
                      const nextStarter = getStarterTemplatePage(selectedTemplate.vertical, event.target.value);
                      navigate(buildAiGenerateHref({
                        template: selectedTemplate.vertical,
                        starter: nextStarter?.id,
                        siteId,
                        source,
                      }), { replace: true });
                    }}
                    className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                  >
                    {starterPages.map((page) => (
                      <option key={page.id} value={page.id}>
                        {page.title}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={pageType}
                    onChange={(event) => setPageType(event.target.value)}
                    className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                  >
                    <option value="site_page">Info page</option>
                    <option value="lead_capture">Lead page</option>
                    <option value="sales">Sales page</option>
                    <option value="booking">Booking page</option>
                    <option value="webinar">Webinar page</option>
                    <option value="thank_you">Thank-you page</option>
                  </select>
                )}
              </label>

              <label className="space-y-2 text-sm">
                <span className="font-medium text-foreground">Business name</span>
                <input
                  value={businessName}
                  onChange={(event) => {
                    setBusinessName(event.target.value);
                    resetBusinessConfirmation();
                  }}
                  placeholder="Bright Smile Dental"
                  className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                />
              </label>

              <label className="space-y-2 text-sm">
                <span className="font-medium text-foreground">Business type</span>
                <input
                  value={businessType}
                  onChange={(event) => {
                    setBusinessType(event.target.value);
                    resetBusinessConfirmation();
                  }}
                  placeholder="Dental clinic"
                  className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                />
              </label>

              <label className="space-y-2 text-sm md:col-span-2">
                <span className="font-medium text-foreground">Location</span>
                <input
                  value={location}
                  onChange={(event) => {
                    setLocation(event.target.value);
                    resetBusinessConfirmation();
                  }}
                  placeholder="Bucharest"
                  className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                />
              </label>

              <div className="rounded-2xl border border-border bg-background/70 px-4 py-3 md:col-span-2">
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Question 2</div>
                <h3 className="mt-1 font-medium text-foreground">What should the page help visitors do?</h3>
                <p className="mt-1 text-sm text-muted-foreground">Offer, audience, and primary action guide the generated sections.</p>
              </div>

              <label className="space-y-2 text-sm md:col-span-2">
                <span className="font-medium text-foreground">What do you want to sell or book?</span>
                <textarea
                  value={primaryOffer}
                  onChange={(event) => setPrimaryOffer(event.target.value)}
                  rows={3}
                  placeholder="Family dentistry, Invisalign, emergency appointments, and annual check-ups."
                  className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                />
              </label>

              <label className="space-y-2 text-sm md:col-span-2">
                <span className="font-medium text-foreground">Who is this page for?</span>
                <textarea
                  value={idealCustomer}
                  onChange={(event) => setIdealCustomer(event.target.value)}
                  rows={3}
                  placeholder="Busy parents and professionals who want a calm clinic with flexible hours."
                  className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                />
              </label>

              <label className="space-y-2 text-sm md:col-span-2">
                <span className="font-medium text-foreground">What should the visitor do next?</span>
                <textarea
                  value={primaryGoal}
                  onChange={(event) => setPrimaryGoal(event.target.value)}
                  rows={2}
                  placeholder="Book a first consultation."
                  className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                />
              </label>

              <label className="space-y-2 text-sm">
                <span className="font-medium text-foreground">Why choose you?</span>
                <textarea
                  value={differentiatorsInput}
                  onChange={(event) => setDifferentiatorsInput(event.target.value)}
                  rows={4}
                  placeholder={"Open Saturdays\nGentle care for anxious patients"}
                  className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                />
              </label>

              <label className="space-y-2 text-sm">
                <span className="font-medium text-foreground">Must-have details</span>
                <textarea
                  value={mustIncludeInput}
                  onChange={(event) => setMustIncludeInput(event.target.value)}
                  rows={4}
                  placeholder={"Online booking\nEmergency call button"}
                  className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                />
              </label>
            </div>

            <div className="rounded-2xl border border-border bg-background p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                <h3 className="font-medium text-foreground">2. Confirm the business details</h3>
                <p className="text-sm text-muted-foreground">
                    We look for the business first so the draft starts from real details instead of guesswork.
                </p>
                </div>
                <button
                  type="button"
                  onClick={handleBusinessLookup}
                  disabled={!businessLookupReady || isLookingUpBusiness}
                  className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:bg-accent disabled:opacity-50"
                >
                  {isLookingUpBusiness ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  Find business details
                </button>
              </div>

              {businessCard ? (
                <div className="mt-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Store className="h-4 w-4 text-emerald-700" />
                        {businessCard.name}
                      </div>
                      <div className="flex items-start gap-2 text-sm text-muted-foreground">
                        <MapPinned className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{businessCard.formattedAddress}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {businessCard.primaryTypeDisplayName ?? "Business listing"}
                        {typeof businessCard.rating === "number" ? ` · ${businessCard.rating.toFixed(1)} stars` : ""}
                        {typeof businessCard.userRatingCount === "number" ? ` · ${businessCard.userRatingCount} reviews` : ""}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {businessCard.nationalPhoneNumber ?? "No phone found"}
                        {businessCard.websiteUri ? ` · ${businessCard.websiteUri}` : ""}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmedBusiness(businessCard);
                          setBusinessDecision("confirmed");
                          setLastAction("Confirmed business details will be used for the draft.");
                        }}
                        className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
                      >
                        Use these details
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmedBusiness(null);
                          setBusinessDecision("manual");
                          setLastAction("We will use your answers only.");
                        }}
                        className="rounded-xl border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:bg-accent"
                      >
                        Use my answers instead
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {businessDecision === "manual" && !businessCard ? (
                <div className="mt-4 rounded-2xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
                  We could not confirm a listing. You can still create the draft from the answers you entered.
                </div>
              ) : null}
            </div>

            {(surfaceError || enrichmentError) ? (
              <div className="flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="space-y-3">
                  <span className="block">{surfaceError ?? enrichmentError}</span>
                  <div className="flex flex-wrap gap-2">
                    {checkpoint ? (
                      <button
                        type="button"
                        onClick={() => void handleRefreshCheckpoint()}
                        disabled={isRefreshing}
                        className="rounded-xl border border-destructive/25 bg-background px-3 py-2 text-sm font-medium text-foreground transition hover:bg-accent disabled:opacity-50"
                      >
                        {isRefreshing ? "Refreshing draft..." : "Refresh draft"}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => setSurfaceError(null)}
                      className="rounded-xl border border-destructive/25 bg-background px-3 py-2 text-sm font-medium text-foreground transition hover:bg-accent"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {lastAction ? (
              <div className="flex items-start gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{lastAction}</span>
              </div>
            ) : null}

            <div className="rounded-2xl border border-border bg-background p-4">
              <div className="space-y-1">
                <h3 className="font-medium text-foreground">3. Create the draft</h3>
                <p className="text-sm text-muted-foreground">
                  AI creates a review draft first. Nothing goes into the editor until you confirm the sections and save it.
                </p>
              </div>
              <button
                type="button"
                data-guide="ai-generate"
                onClick={handleGenerate}
                disabled={!canGenerate || isGenerating || isRefreshing}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
              >
                {(isGenerating || isRefreshing) ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Create draft
              </button>
              <p className="mt-3 text-sm text-muted-foreground">{draftReadinessHint}</p>
            </div>
          </div>
        </section>

        <section className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Your draft</h2>
              <p className="text-sm text-muted-foreground">
                Review each section below. Nothing is saved until you confirm every section and choose Save draft to editor.
              </p>
            </div>
            {checkpoint ? (
              <span className="rounded-full border border-border px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
                {checkpoint.status === "pending" ? "draft ready" : checkpoint.status}
              </span>
            ) : null}
          </div>

          {!checkpoint ? (
            streamedSections.length > 0 || streamProgress ? (
              <div className="space-y-3 rounded-[20px] border border-dashed border-primary/30 bg-primary/5 p-5">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium text-foreground">Streaming section-by-section draft</span>
                  {streamProgress ? (
                    <span className="text-muted-foreground">{streamProgress.current} / {streamProgress.total}</span>
                  ) : null}
                </div>
                {streamedSections.map((section, index) => (
                  <div key={`${section.type}-${index}`} className="rounded-2xl border border-border bg-background px-4 py-3">
                    <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Section {index + 1}</div>
                    <div className="mt-1 font-medium text-foreground">{getSectionReviewTitle(section, index)}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{getSectionReviewSummary(section)}</p>
                  </div>
                ))}
                <p className="text-sm text-muted-foreground">The final checkpoint will appear here automatically when all sections finish.</p>
              </div>
            ) : (
              <div className="rounded-[20px] border border-dashed border-border bg-muted/30 p-8 text-sm text-muted-foreground">
                Complete the interview and confirm the business details first. Your draft will appear here for review.
              </div>
            )
          ) : (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-4">
                {progressSteps.map((step, index) => (
                  <div
                    key={step.id}
                    className={`rounded-2xl border px-4 py-3 ${
                      step.done
                        ? "border-emerald-500/20 bg-emerald-500/5"
                        : step.active
                          ? "border-primary/25 bg-primary/5"
                          : "border-border bg-background"
                    }`}
                  >
                    <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Step {index + 1}</div>
                    <div className="mt-2 text-sm font-semibold text-foreground">{step.label}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {step.done ? "Done" : step.active ? "You are here" : "Up next"}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-border bg-background px-4 py-3">
                  <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Revisions</div>
                  <div className="mt-2 text-2xl font-semibold text-foreground">{checkpoint.versions.length}</div>
                </div>
                <div className="rounded-2xl border border-border bg-background px-4 py-3">
                  <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Sections</div>
                  <div className="mt-2 text-2xl font-semibold text-foreground">{sections.length}</div>
                </div>
                <div className="rounded-2xl border border-border bg-background px-4 py-3">
                  <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Needs review</div>
                  <div className="mt-2 text-2xl font-semibold text-foreground">{unmappedCount}</div>
                  {unmappedCount > 0 ? (
                    <p className="mt-1 text-xs text-muted-foreground">Fields that need manual attention in the editor.</p>
                  ) : null}
                </div>
              </div>

              <div className="rounded-2xl border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">AI Feedback</div>
                    <div className="mt-1 font-medium text-foreground">How useful is this draft?</div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Rate the current draft so page-builder AI learns which outputs help before anything is approved.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => { void handleDraftFeedbackRating(rating); }}
                        className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                          draftFeedbackRating === rating
                            ? "border-[var(--color-ai)] bg-[var(--color-ai)] text-white"
                            : "border-border bg-background text-foreground hover:bg-accent"
                        }`}
                      >
                        {rating}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-background p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="font-medium text-foreground">Review each section</h3>
                  <span className="text-xs text-muted-foreground">
                    {sectionApproval.approvedCount} of {sectionApproval.totalCount} confirmed
                  </span>
                </div>
                {sections.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No reviewable sections were generated. Try revising your answers or creating the draft again.</p>
                ) : (
                  <div className="space-y-3">
                    {sections.map((section, index) => {
                      const reviewKey = getSectionReviewKey(checkpoint.currentVersion, section, index);
                      const decision = sectionDecisions[reviewKey];
                      const isUpdatingSection = sectionActionKey === reviewKey;
                      const showFeedback = activeFeedbackKey === reviewKey;

                      return (
                        <div key={`${section.type}-${index}`} className="rounded-xl border border-border bg-muted/20 p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Section {index + 1}</div>
                              <div className="mt-1 font-medium text-foreground">{getSectionReviewTitle(section, index)}</div>
                              <p className="mt-1 text-sm text-muted-foreground">{getSectionReviewSummary(section)}</p>
                            </div>
                            <span className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground">
                              {decision === "approved"
                                ? "Looks good"
                                : decision === "change"
                                  ? "Needs changes"
                                  : decision === "redo"
                                    ? "Redo requested"
                                    : "Not reviewed"}
                            </span>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => handleSectionDecision(reviewKey, "approved")}
                              className="rounded-xl border border-border px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-accent"
                            >
                              Looks good
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                handleSectionDecision(reviewKey, "change");
                                setActiveFeedbackKey(reviewKey);
                              }}
                              className="rounded-xl border border-border px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-accent"
                            >
                              Change something
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSectionRedo(section, index, reviewKey)}
                              disabled={isUpdatingSection}
                              className="rounded-xl border border-border px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-accent disabled:opacity-50"
                            >
                              {isUpdatingSection ? "Redoing…" : "Redo"}
                            </button>
                          </div>

                          {showFeedback ? (
                            <div className="mt-3 space-y-2">
                              <textarea
                                value={sectionFeedback[reviewKey] ?? ""}
                                onChange={(event) => setSectionFeedback((current) => ({
                                  ...current,
                                  [reviewKey]: event.target.value,
                                }))}
                                rows={3}
                                placeholder="Describe what you want changed in this section."
                                className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                              />
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleSectionChange(section, index, reviewKey)}
                                  disabled={!sectionFeedback[reviewKey]?.trim() || isUpdatingSection}
                                  className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
                                >
                                  {isUpdatingSection ? "Saving…" : "Update this section"}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setActiveFeedbackKey(null)}
                                  disabled={isUpdatingSection}
                                  className="rounded-xl border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:bg-accent disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-border bg-background p-4">
                <h3 className="mb-3 font-medium text-foreground">Revise the entire draft</h3>
                <p className="mb-2 text-sm text-muted-foreground">
                  Describe what you want changed across the whole page. A new version will be created for review.
                </p>
                <textarea
                  value={iterationNotes}
                  onChange={(event) => setIterationNotes(event.target.value)}
                  rows={4}
                  placeholder="Example: keep the hero, add stronger social proof, and rewrite the call-to-action for appointment bookings."
                  className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                />
                <button
                  type="button"
                  onClick={handleIterate}
                  disabled={!iterationNotes.trim() || isIterating}
                  className="mt-3 inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:bg-accent disabled:opacity-50"
                >
                  {isIterating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Revise draft
                </button>
              </div>

              <div className="rounded-2xl border border-border bg-background p-4">
                <h3 className="mb-3 font-medium text-foreground">Finalize and open in the editor</h3>
                <p className="mb-4 text-sm text-muted-foreground">
                  Save this draft only after every section looks right. You will continue in the guided editor, not a blank builder.
                </p>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-foreground">Page title</span>
                    <input
                      value={title}
                      onChange={(event) => {
                        const nextTitle = event.target.value;
                        setTitle(nextTitle);
                        if (!isSlugManuallyEdited) {
                          setSlug(slugify(nextTitle));
                        }
                      }}
                      className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                    />
                  </label>
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-foreground">Slug</span>
                    <input
                      value={slug}
                      onChange={(event) => {
                        setSlug(slugify(event.target.value));
                        setIsSlugManuallyEdited(true);
                      }}
                      className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
                    />
                  </label>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleReject}
                    disabled={isRejecting || checkpoint.status !== "pending"}
                    className="inline-flex items-center gap-2 rounded-xl border border-destructive/25 px-4 py-2 text-sm font-medium text-destructive transition hover:bg-destructive/5 disabled:opacity-50"
                  >
                    {isRejecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                    Discard draft
                  </button>
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={!title.trim() || !slug.trim() || !sectionApproval.allApproved || isApproving || checkpoint.status !== "pending"}
                    className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
                  >
                    {isApproving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                    Save and open in editor
                  </button>
                </div>
                {checkpoint.status !== "pending" && approvedPageId ? (
                  <div className="mt-3 flex items-start gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <span>This draft was already saved.</span>
                      <button
                        type="button"
                        onClick={handleNavigateToApprovedPage}
                        className="ml-2 font-medium underline transition hover:no-underline"
                      >
                        Open it in the editor
                      </button>
                    </div>
                  </div>
                ) : checkpoint.status !== "pending" ? (
                  <p className="mt-3 text-sm text-muted-foreground">
                    This draft has already been {checkpoint.status}. Create a new one from the interview above.
                  </p>
                ) : !sectionApproval.allApproved ? (
                  <p className="mt-3 text-sm text-muted-foreground">
                    Mark every section as &quot;Looks good&quot; before saving this draft.
                  </p>
                ) : null}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
